import { Link } from '@tanstack/react-router';
import { postMeta } from './manifest';
import type { Post } from './types';
import { Callout, H2, H3, OL, P, Pull, UL } from './ui';

const post: Post = { meta: postMeta('producer-battle-vs-beat-battle-net'), Body };
export default post;

function Body() {
  return (
    <>
      <P>
        Two platforms, both for producers who want timed scored battles. Both are real-time,
        browser-based, and ask you to make a beat in your DAW and upload it. The differences in what
        sits around that core loop are big enough that the choice is genuinely easy.
      </P>
      <P>
        Producer Battle is the deeper platform. More modes, more genres, weekly bracket tournaments,
        an async Daily Challenge, Sample Flip rounds, per-genre ranking ladders, season rewards,
        producer-grade profiles, and a real anti-griefing system. beat-battle.net is a polished
        casual battle game with a ranked queue and a cosmetic shop - fun, well-made, but built to a
        smaller spec. If you came here to figure out which one fits a serious producer better, the
        rest of this post is the breakdown of why it's us.
      </P>

      <H2 id="quick-version">The quick version</H2>
      <UL>
        <li>
          <strong>Both</strong>: real-time, in-browser, DAW-uploaded beats, ranked queue, public
          leaderboard, private code-only rooms.
        </li>
        <li>
          <strong>Producer Battle only</strong>: Sample Flip mode, async Daily Challenge, weekly
          bracket tournaments with a public showcase phase, ten seeded genres (plus
          community-proposed) with per-genre ranked ladders, gold/silver/bronze season rewards, an
          honor-based anti-griefing system, producer-grade profiles (bio, social links, pinned
          tracks, win/loss), three sign-in providers (email, Google, Discord).
        </li>
        <li>
          <strong>beat-battle.net only</strong>: Creative Mode (prompt-driven round), Beatbucks shop
          with profile themes, Code Display for streamers. Their patch cadence is tight (currently
          v2.2.1).
        </li>
      </UL>

      <H2 id="format">Format and what a session actually looks like</H2>
      <H3>Producer Battle</H3>
      <P>
        Open{' '}
        <Link to="/play" className="underline decoration-primary/50 underline-offset-2">
          /play
        </Link>{' '}
        and pick a mode. Quickplay finds the next open lobby across active genres. Ranked picks a
        random system genre and a curated sample pack for that genre. Private opens a host-run room
        you can size from 2 to 8, listed publicly in{' '}
        <Link to="/lobbies" className="underline decoration-primary/50 underline-offset-2">
          /lobbies
        </Link>{' '}
        if you want strangers, or kept code-only if you want a closed crew. Submit timer is tuned
        per genre (short bangers get a tighter window, arrangement-heavy styles get more room).
      </P>
      <P>
        You build the beat in your DAW, export to MP3 or WAV, and upload before the timer ends.
        Voting is blind: producers and audience score every other entry, names hidden until results
        post. Glicko-style rating updates per genre - your trap rating and your house rating are
        separate ladders.
      </P>
      <H3>beat-battle.net</H3>
      <P>
        <a
          href="https://beat-battle.net"
          rel="noreferrer nofollow noopener"
          target="_blank"
          className="underline decoration-primary/50 underline-offset-2"
        >
          beat-battle.net
        </a>{' '}
        runs Quick Battle and Ranked queues from the same lobby UI. The round flows through lobby,
        battle, showcase and results stages. You upload your beat (auto-submits with a replace
        window during the upload phase), then vote. ELO updates after the round. Private code-only
        rooms work the same way with host controls for max players, timer, and visibility.
      </P>

      <H2 id="anti-griefing">Anti-griefing - the biggest gap</H2>
      <P>
        This is where the platforms diverge most sharply.{' '}
        <strong>On beat-battle.net, leaving a match has no rated consequence.</strong> A producer
        can queue, see what they're up against, and bail without losing anything. That makes ranked
        feel casual, but it also means a meaningful slice of your matches end with empty submission
        slots.
      </P>
      <P>
        Producer Battle ships an honor score, separate from rating, that tracks whether you finish
        what you queue for. Abandoning a ranked match costs honor. Never voting costs honor. Ghost-
        queueing costs honor. Drop your honor far enough and ranked locks; drop it further and
        tournament play locks too. Penalties scale with how disruptive the abandon was - bailing
        before submit is treated differently from no-show voting. Clean play regenerates over time.
        On top of that we run a vote-ring auto-detector and a cluster guard that won't drop
        suspected smurfs into the same lobby as their main account.
      </P>
      <P>
        If you want a ladder where rating means something because everyone in your match showed up,
        this gap is hard to overstate.
      </P>

      <H2 id="modes">Modes</H2>
      <P>
        Producer Battle ships seven modes; beat-battle.net ships three or four. The breadth
        difference is real and is the second-biggest argument for picking one over the other.
      </P>
      <H3>Producer Battle</H3>
      <UL>
        <li>
          <strong>Quickplay</strong> - the next open lobby across all active genres.
        </li>
        <li>
          <strong>Ranked</strong> - rated, Glicko-style, picks a random system genre.
        </li>
        <li>
          <strong>Private</strong> - host-run room, 2-8 players, optionally listed in /lobbies for
          strangers or kept code-only for a closed crew. Host picks the submit duration from a
          preset list and starts the match once every seated player has marked ready.
        </li>
        <li>
          <strong>Tournament</strong> - scheduled weekly events, see below.
        </li>
        <li>
          <strong>Sample Flip</strong> - unique to us. Everyone in the lobby flips one shared source
          loop pulled from a curated pool. Same source, totally different beats.
        </li>
        <li>
          <strong>Daily Challenge</strong> - unique to us. Async, system-picked genre. Submit one
          day, vote the next, daily champion crowned at rollover. Up to 20 producers per day.
        </li>
        <li>
          <strong>Practice</strong> - solo, no opponents, useful for stress-testing your export flow
          against the timer.
        </li>
      </UL>
      <H3>beat-battle.net</H3>
      <UL>
        <li>
          <strong>Quick Battle</strong> - casual queue, multiplayer.
        </li>
        <li>
          <strong>Ranked</strong> - ELO queue, auto-queue toggle, narrow genre picker (about two
          options).
        </li>
        <li>
          <strong>Code-only</strong> rooms - private host-run lobbies with timer/visibility/max-
          players controls and a streamer-friendly Code Display.
        </li>
        <li>
          <strong>Creative Mode</strong> - prompt-driven mode where players cook to a wild brief
          delivered at round kick-off. Genuinely fun, and they own that format.
        </li>
      </UL>
      <P>
        No Sample Flip, no Daily Challenge, no weekly bracket tournament product on beat-battle.net.
        If those formats appeal to you, the choice is made.
      </P>

      <H2 id="tournament">Tournaments</H2>
      <P>
        Producer Battle runs scheduled{' '}
        <Link to="/tournaments" className="underline decoration-primary/50 underline-offset-2">
          weekly tournaments
        </Link>{' '}
        per genre. Producers submit a single round-1 track during the tournament window, the track
        opens to the community for a multi-day showcase phase where the public scores it, and a
        bracket runs across the showcase qualifiers. Some tournaments run as showcase-only (no
        bracket) for exhibition events. Honor gates participation.
      </P>
      <P>
        beat-battle.net's homepage advertises Quick Battle and Ranked. There's a showcase stage
        inside every match (a listening window between battle and results), but no separate weekly
        bracket tournament product. If they run them at all, it's via Discord rather than as a
        first-class site feature.
      </P>

      <H2 id="genre">Genre coverage and per-genre ranking</H2>
      <P>
        Producer Battle seeds ten system genres on{' '}
        <Link to="/genres" className="underline decoration-primary/50 underline-offset-2">
          /genres
        </Link>
        : Hip-hop / Trap, Phonk, House, Amapiano, Afro-house, Drum and Bass, Dubstep / Riddim,
        Techno, Hardstyle, and Lo-fi. Each carries a format config tuned to that style. On top of
        that, producers can{' '}
        <Link to="/genres/propose" className="underline decoration-primary/50 underline-offset-2">
          propose new genres
        </Link>{' '}
        which go through community voting before promotion. Rating ladders are <em>per genre</em> -
        your trap rating, your house rating, your phonk rating live on separate boards. A house
        producer is never being scored against a trap standard.
      </P>
      <P>
        beat-battle.net's ranked queue ships with a much narrower genre selection - effectively two
        options - and there's no public path for producers to propose more. If your style doesn't
        fit one of those buckets, your only competitive surface there is "general" beat battles
        where a phonk producer is rated against a house producer against a trap producer.
      </P>

      <H2 id="sample-kit">Sample kits</H2>
      <P>
        Producer Battle ships every match with a sample pack. System-curated pool packs back ranked
        and quickplay; producers can also upload their own packs for private rooms; admin-generated
        kits round out the catalogue. The constraint is that everyone in the match works from the
        same raw material - audience votes are comparing creative choices, not who has better loops.
      </P>
      <P>
        beat-battle.net's public homepage and bundle make no mention of a shared kit. From outside
        the lobby, the format reads as "make a beat in your style, upload it" rather than "here's
        the kit, flip it." If you care about shared-source-material constraint, Producer Battle is
        the one explicitly built around it.
      </P>

      <H2 id="ranking">Ranking, tiers, and season rewards</H2>
      <P>
        beat-battle.net's leaderboard shows ELO per player. Producer Battle uses a Glicko-style
        rating that adds rating-uncertainty and volatility, so new accounts and inactive players
        move faster while established producers move slower. Crucially, ratings are per genre, and
        each season top finishers earn gold, silver, or bronze season-reward tiers shown on the
        public{' '}
        <Link to="/leaderboard" className="underline decoration-primary/50 underline-offset-2">
          leaderboard
        </Link>{' '}
        and on their profile. After enough ranked matches a producer also lands in a competitive
        tier - bronze through grandmaster - based on rating.
      </P>

      <H2 id="voting">Voting</H2>
      <P>
        Producer Battle uses a Tinder-style swipe stack: swipe right to commit a top score, arrow
        keys 1-5 on desktop, swipe left to skip. You score every other entry, not just your top
        three. That gives finer signal - a round with one obvious winner and seven mid-tier beats
        looks different from a round where the top four are tight.
      </P>
      <P>
        beat-battle.net keeps voting fast and decisive inside their match flow. Both platforms run
        anti-manipulation rules; we additionally run a vote-ring auto-detector and a cluster guard
        as part of the anti-griefing layer above.
      </P>

      <H2 id="profile">Public profile</H2>
      <P>
        Producer Battle profiles at <code>/@handle</code> show your bio, social links (Spotify,
        SoundCloud, Bandcamp, Instagram, YouTube, website), pinned tracks, win/loss record,
        achievements, season-reward badges, and per-genre rank. They're built as a portfolio you can
        put in your bio - linkable, indexable, and producer-first.
      </P>
      <P>
        beat-battle.net profiles lean cosmetic: a Beatbucks shop sells profile themes and the v2
        update wired a per-profile theme system across the menu UI. Producer Battle has its own
        cosmetic shop with profile displays on the roadmap, so this is less a structural advantage
        and more a "they shipped first" point. Where the platforms differ structurally is the
        portfolio depth - bio, socials, pinned tracks, season rewards - that ours is built around.
      </P>

      <H2 id="entry-barrier">Getting started</H2>
      <OL>
        <li>
          <strong>Producer Battle</strong>: sign up at{' '}
          <Link to="/auth/sign-up" className="underline decoration-primary/50 underline-offset-2">
            /auth/sign-up
          </Link>{' '}
          with email + password, Google, or Discord - three providers, your call. Free. Open{' '}
          <Link to="/play" className="underline decoration-primary/50 underline-offset-2">
            /play
          </Link>{' '}
          and the matchmaker handles the rest. Guests can play a few matches per day without an
          account.
        </li>
        <li>
          <strong>beat-battle.net</strong>: sign in with Discord or a legacy email/password, free
          tier available, then Quick Battle from the main menu. Active Discord at{' '}
          <code>discord.gg/beatbattle</code>.
        </li>
      </OL>

      <H2 id="strengths-weaknesses">The verdict, side by side</H2>
      <H3>Producer Battle</H3>
      <UL>
        <li>
          <strong>Strength:</strong> seven modes including Sample Flip, async Daily Challenge, and
          weekly bracket tournaments with a public showcase phase. Real competitive depth.
        </li>
        <li>
          <strong>Strength:</strong> proper anti-griefing - honor score, ranked/tournament locks,
          vote-ring detection, anti-smurf cluster guard. Quitters get punished, not shrugged at.
        </li>
        <li>
          <strong>Strength:</strong> per-genre rating ladders, season-reward tiers (gold / silver /
          bronze) shown on profile and the public leaderboard.
        </li>
        <li>
          <strong>Strength:</strong> producer-grade public profiles with bio, social links, pinned
          tracks, achievements, win/loss, and per-genre rank. Linkable from your IG bio.
        </li>
        <li>
          <strong>Strength:</strong> shared sample-pack model across every match.
        </li>
        <li>
          <strong>Strength:</strong> voice-announced upload reminders during the submit window so
          you don't lose a match staring at the export dialog.
        </li>
      </UL>
      <H3>beat-battle.net</H3>
      <UL>
        <li>
          <strong>Strength:</strong> polished and established. Lobby/round flow is well-tuned and
          the UI feel is tight.
        </li>
        <li>
          <strong>Strength:</strong> Beatbucks shop and profile themes are live today. If collecting
          cosmetics is what hooks you, they have a product for that right now.
        </li>
        <li>
          <strong>Strength:</strong> Creative Mode is genuinely fun and a format we don't ship yet.
        </li>
        <li>
          <strong>Weakness:</strong> no anti-griefing teeth - quitters bail without paying.
        </li>
        <li>
          <strong>Weakness:</strong> only three or four modes. No Sample Flip, no Daily Challenge,
          no public weekly bracket tournament product.
        </li>
        <li>
          <strong>Weakness:</strong> ranked queue exposes about two genres. A producer outside those
          styles is competing in a genre-mixed pool.
        </li>
        <li>
          <strong>Weakness:</strong> profile pages are cosmetic-first, not portfolio-first.
        </li>
      </UL>

      <Pull>
        beat-battle.net is the polished casual platform. Producer Battle is the platform with depth
        - more modes, real tournaments, real consequences for quitting, real per-genre ranking, and
        a public profile you can actually link from your bio.
      </Pull>

      <H2 id="decision-matrix">Who should pick which</H2>
      <Callout>
        Pick <strong>Producer Battle</strong> if you take this seriously - if you want per-genre
        ranking, weekly bracket tournaments, async daily challenges, sample-flip rounds, season
        rewards, an honor system that actually punishes quitters, or a producer profile you can put
        in your bio. This is the default recommendation for any producer reading this post.
      </Callout>
      <Callout>
        Pick <strong>beat-battle.net</strong> only if your priority is collecting profile-theme
        cosmetics in their Beatbucks shop, you specifically want their Creative Mode prompt format,
        or you want to play both for variety.
      </Callout>
      <Callout>
        Honest TL;DR: try Producer Battle first. If after a few rounds you find yourself wanting a
        cosmetic shop or the Creative Mode prompt format, run a session on beat-battle.net too. Most
        producers won't go back.
      </Callout>

      <H2 id="what-they-share">What both platforms share</H2>
      <P>
        Both are free to enter, browser-based, real-time, with private code-only lobbies and a
        public leaderboard. Both use audience voting and a showcase/results stage. Whichever you
        pick, the core motivation is the same: a timed, scored comparison against other producers is
        a faster feedback loop than dropping SoundCloud links and hoping.
      </P>
      <P>
        For wider context, the{' '}
        <Link
          to="/blog/$slug"
          params={{ slug: 'best-online-beat-battle-sites' }}
          className="underline decoration-primary/50 underline-offset-2"
        >
          best online beat battle sites
        </Link>{' '}
        post covers more platforms, and{' '}
        <Link
          to="/blog/$slug"
          params={{ slug: 'what-is-a-beat-battle' }}
          className="underline decoration-primary/50 underline-offset-2"
        >
          what is a beat battle
        </Link>{' '}
        explains the format from scratch.
      </P>
    </>
  );
}
